<template>
  <div>
    <AppStack
      direction="flex-col"
      class="h-full px-4 py-4 bg-bg-elevation-0-breaker gap-3 border-t border-x-0 border-b-0
        border-border-elevation-0-primary"
    >
      <AppStack class="gap-1">
        <AppTypography
          :text="props.title"
          name="text-md-4-semibold"
          color="text-fg-elevation-2-primary"
        />
        <AppTypography
          :text="`${props.currentStep}/${props.totalSteps}`"
          name="text-md-4-medium"
          color="text-fg-elevation-2-tertiary"
        />
      </AppStack>
      <AppStack class="gap-[6px]">
        <OrderModalMenuItemsItemCell
          v-for="(option, optionKey) in props.options"
          :key="optionKey"
          :title="translateField(option.name)"
          :subtitle="`${formatPrice(option.price)}`"
          :stopped="!!stopEntryOf(option)"
          :stop-label="props.stopLabel"
          class="flex-1 min-w-[32%]"
          @menu-item-tap="
            emit('menu-item-option-tap', {
              option,
              onClick: $event.onClick,
            })
          "
          @stopped-tap="
            emit('option-stopped-tap', {
              option,
              entry: stopEntryOf(option)!,
            })
          "
        />
        <div
          v-for="ghost in 3 - (options.length % 3)"
          :key="ghost"
          class="flex-1 min-w-[32%]"
        />
      </AppStack>
    </AppStack>
  </div>
</template>

<script lang="ts" setup>
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import OrderModalMenuItemsItemCell from './OrderModalMenuItemsItemCell.vue'
import { type Stores } from '~/composables/useStores'
import useAppHelpers from '~/composables/useAppHelpers'
import { type ExtractEmits } from '@restify/packages/types'
import type { StopEntry } from '@restify/packages/dist/types/typebox/menu-items.schema'

// Inlined instead of `typeof props.options[number]` — the props macro
// hasn't been declared yet at this point and a forward reference makes
// the whole `props` declaration implicitly `any`.
type OptionOfMenuItem =
  | Stores['menu-items']['Result']['variations'][0]['options'][0]
  | Stores['menu-items']['Result']['extras'][0]['options'][0]

const emit = defineEmits<{
  'menu-item-option-tap': [
    {
      option: OptionOfMenuItem,
      onClick: Parameters<ExtractEmits<typeof OrderModalMenuItemsItemCell>['menu-item-tap']>[0]['onClick']
    }
  ]
  'option-stopped-tap': [
    { option: OptionOfMenuItem; entry: StopEntry }
  ]
}>()

const props = withDefaults(
  defineProps<{
    title: string
    currentStep: number
    totalSteps: number
    options: Array<OptionOfMenuItem>
    // Resolver injected by the parent so the Configurator doesn't
    // need to know about storefronts or auth state. Returns the live
    // stop entry (reason + comment) or null when the option is
    // orderable. Extras have no stops in schema so this returns null
    // for them; the cell just renders as usual.
    optionStopEntry?: (option: OptionOfMenuItem) => StopEntry | null
    stopLabel?: string
  }>(),
  {
    optionStopEntry: () => null,
    stopLabel: '',
  },
)

const { translateField, formatPrice } = useAppHelpers()

const stopEntryOf = (option: OptionOfMenuItem): StopEntry | null =>
  props.optionStopEntry(option)
</script>
